import { Article, Close, UploadFile } from '@mui/icons-material';
import {
    Box,
    CircularProgress,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Tooltip,
    Typography,
} from '@mui/material';
import { ChangeEvent, DragEvent, FC, useRef, useState } from 'react';
import { QuoteAttachmentDto } from '../../../dtos/QuoteAttachmentDto';
import { useGetQuoteAttachmentsQuery } from '../../../store/generated/generatedApi';
import { IQuotePendingAttachmentFile } from './useQuoteForm';

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];

interface IQuoteAttachmentsSectionProps {
    quoteId: string;
    pendingFiles: IQuotePendingAttachmentFile[];
    pendingRemovalIds: string[];
    onAddPendingFiles: (files: File[]) => void;
    onRemovePendingFile: (localId: string) => void;
    onStageRemoval: (attachment: QuoteAttachmentDto) => void;
    attachmentSaveError: string | null;
    attachmentSyncInProgress: boolean;
}

const formatFileSize = (bytes: number | null): string => {
    if (bytes === null || bytes === undefined) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isImageType = (fileType: string): boolean =>
    fileType?.startsWith('image/') ?? false;

export const QuoteAttachmentsSection: FC<IQuoteAttachmentsSectionProps> = ({
    quoteId,
    pendingFiles,
    pendingRemovalIds,
    onAddPendingFiles,
    onRemovePendingFile,
    onStageRemoval,
    attachmentSaveError,
    attachmentSyncInProgress,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploadErrors, setUploadErrors] = useState<string[]>([]);
    const [isDragOver, setIsDragOver] = useState(false);

    const { data: attachments = [], isLoading } = useGetQuoteAttachmentsQuery(
        { quoteId },
        { skip: !quoteId },
    );

    const visibleServerAttachments = attachments.filter((a) => !pendingRemovalIds.includes(a.id));

    const validateFile = (file: File): string | null => {
        if (!ALLOWED_MIME_TYPES.includes(file.type)) {
            return 'Only PDF and image files are supported.';
        }
        if (file.size > MAX_FILE_SIZE_BYTES) {
            return 'File must be under 10 MB.';
        }
        return null;
    };

    const queueValidatedFiles = (files: FileList | File[]) => {
        const fileArray = Array.from(files);
        const newErrors: string[] = [];
        const toAdd: File[] = [];

        for (const file of fileArray) {
            const validationError = validateFile(file);
            if (validationError) {
                newErrors.push(`${file.name}: ${validationError}`);
            } else {
                toAdd.push(file);
            }
        }

        if (toAdd.length > 0) {
            onAddPendingFiles(toAdd);
        }

        if (newErrors.length > 0) {
            setUploadErrors((prev) => [...prev, ...newErrors]);
        }
    };

    const handleFileDialogUpload = (event: ChangeEvent<HTMLInputElement>) => {
        setUploadErrors([]);
        if (event.target.files && event.target.files.length > 0) {
            queueValidatedFiles(event.target.files);
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
        event.stopPropagation();
        event.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
        event.stopPropagation();
        event.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
        event.stopPropagation();
        event.preventDefault();
        setIsDragOver(false);
        setUploadErrors([]);
        if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
            queueValidatedFiles(event.dataTransfer.files);
        }
    };

    const noActiveRows =
        visibleServerAttachments.length === 0 && pendingFiles.length === 0;
    const listAreaLoading = isLoading || attachmentSyncInProgress;
    const showEmptyMessage = !listAreaLoading && noActiveRows;
    const loadingMessage = attachmentSyncInProgress
        ? 'Updating attachments...'
        : 'Loading attachments...';

    return (
        <Grid container direction='column' spacing={2}>
            <Grid item>
                {attachmentSaveError && (
                    <Typography variant='body2' color='error' py={1}>
                        {attachmentSaveError}
                    </Typography>
                )}
                {listAreaLoading ? (
                    <Box display='flex' alignItems='center' gap={1} py={2}>
                        <CircularProgress size={20} />
                        <Typography variant='body2' color='text.secondary'>
                            {loadingMessage}
                        </Typography>
                    </Box>
                ) : showEmptyMessage ? (
                    <Typography variant='body2' color='text.secondary' py={1}>
                        No files attached yet.
                    </Typography>
                ) : !noActiveRows ? (
                    <List dense disablePadding>
                        {visibleServerAttachments.map((attachment) => (
                            <ListItem
                                key={attachment.id}
                                disableGutters
                                secondaryAction={
                                    <Tooltip title='Remove'>
                                        <IconButton
                                            edge='end'
                                            size='small'
                                            onClick={() => onStageRemoval(attachment)}
                                            aria-label={`Remove ${attachment.fileName}`}>
                                            <Close fontSize='small' />
                                        </IconButton>
                                    </Tooltip>
                                }
                                sx={{ borderBottom: '1px solid', borderColor: 'divider', pr: 5 }}>
                                {isImageType(attachment.fileType) ? (
                                    <Box
                                        component='img'
                                        src={attachment.fileUrl}
                                        alt={attachment.fileName}
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            objectFit: 'contain',
                                            mr: 1,
                                            flexShrink: 0,
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                        }}
                                    />
                                ) : (
                                    <ListItemIcon sx={{ minWidth: 48 }}>
                                        <Article color='primary' />
                                    </ListItemIcon>
                                )}
                                <ListItemText
                                    primary={attachment.fileName}
                                    secondary={formatFileSize(attachment.fileSize)}
                                    primaryTypographyProps={{ noWrap: true, title: attachment.fileName }}
                                />
                            </ListItem>
                        ))}
                        {pendingFiles.map((pending) => (
                            <ListItem
                                key={pending.localId}
                                disableGutters
                                secondaryAction={
                                    <Tooltip title='Remove'>
                                        <IconButton
                                            edge='end'
                                            size='small'
                                            onClick={() => onRemovePendingFile(pending.localId)}
                                            aria-label={`Remove ${pending.file.name}`}>
                                            <Close fontSize='small' />
                                        </IconButton>
                                    </Tooltip>
                                }
                                sx={{ borderBottom: '1px solid', borderColor: 'divider', pr: 5 }}>
                                {isImageType(pending.file.type) ? (
                                    <Box
                                        component='img'
                                        src={pending.previewUrl}
                                        alt={pending.file.name}
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            objectFit: 'contain',
                                            mr: 1,
                                            flexShrink: 0,
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                        }}
                                    />
                                ) : (
                                    <ListItemIcon sx={{ minWidth: 48 }}>
                                        <Article color='primary' />
                                    </ListItemIcon>
                                )}
                                <ListItemText
                                    primary={pending.file.name}
                                    secondary={formatFileSize(pending.file.size)}
                                    primaryTypographyProps={{ noWrap: true, title: pending.file.name }}
                                />
                            </ListItem>
                        ))}
                    </List>
                ) : null}
            </Grid>

            <Grid item>
                <input
                    ref={fileInputRef}
                    id='quote-attachment-file-input'
                    type='file'
                    accept='.pdf,image/*'
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleFileDialogUpload}
                />
                <label htmlFor='quote-attachment-file-input'>
                    <Box
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        sx={{
                            width: '100%',
                            minHeight: 120,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 1,
                            border: '2px dashed',
                            borderColor: isDragOver ? 'primary.main' : 'grey.400',
                            backgroundColor: isDragOver ? 'action.hover' : '#f5f5f5',
                            borderRadius: 1,
                            cursor: 'pointer',
                            transition: 'border-color 0.2s, background-color 0.2s',
                            py: 2,
                        }}>
                        <>
                            <UploadFile color='action' fontSize='large' />
                            <Typography variant='body2' color='text.secondary'>
                                Drag & drop files here, or click to browse
                            </Typography>
                            <Typography variant='caption' color='text.disabled'>
                                PDF and image files only · Max 10 MB each · Saved with the quote
                            </Typography>
                        </>
                    </Box>
                </label>
            </Grid>

            {uploadErrors.length > 0 && (
                <Grid item>
                    {uploadErrors.map((err, idx) => (
                        <Typography key={idx} variant='body2' color='error'>
                            {err}
                        </Typography>
                    ))}
                </Grid>
            )}
        </Grid>
    );
};
